/**
 * Saving a report to the user's machine.
 *
 * WHY THIS IS NOT AN ANCHOR TAG
 * -----------------------------
 * The obvious version — `<a href="/api/reports/tax?format=pdf" download>` —
 * cannot work here. A browser following a link sends cookies, not an
 * Authorization header, and every report endpoint is behind a bearer token. It
 * would download a 401 page named "tax.pdf", which opens to nothing and looks
 * like a broken PDF rather than a refused request.
 *
 * So the file is fetched the same way every other request is, held in memory,
 * and handed to the browser as a blob.
 *
 * WHY THE FILENAME IS TAKEN FROM THE SERVER
 * -----------------------------------------
 * The server already names the file after the report and the dates it covers,
 * and it is the only side that knows what those are once the query has been
 * interpreted. Naming it again here would be a second implementation to drift
 * from the first.
 */
import { ApiError, tokens } from './api.js';

// The same base the rest of the client uses; a second spelling of the
// variable name would work in development and fail wherever it is deployed.
const BASE = import.meta.env.VITE_API_BASE ?? '';

/**
 * Pull `filename="..."` out of a Content-Disposition header.
 *
 * Falls back rather than throwing: a download that arrives with an awkward
 * name is a small annoyance, and a download that fails is a large one.
 */
export function filenameFrom(disposition, fallback) {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition ?? '');
  return match ? decodeURIComponent(match[1]) : fallback;
}

/**
 * Fetch a file and save it.
 *
 * The token is read here rather than passed in, so a caller cannot forget it
 * and produce a download that silently saves a refusal.
 *
 * @param {string} path      an API path, e.g. /reports/tax?format=pdf
 * @param {string} fallback  the name to use if the server does not give one
 */
export async function downloadFile(path, fallback) {
  const token = tokens.access;

  let response;
  try {
    response = await fetch(`${BASE}/api${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {
    throw new ApiError('Cannot reach the branch computer.', { offline: true });
  }

  if (!response.ok) {
    /**
     * A refusal arrives as JSON even when a PDF was asked for, so it is read
     * as JSON rather than saved. Saving it would produce a file named
     * "Tax-report.pdf" containing the words "not allowed", which the reader
     * meets as a corrupt document rather than as an answer.
     */
    const problem = await response.json().catch(() => null);
    throw new ApiError(problem?.error ?? `That report could not be produced (${response.status}).`, {
      status: response.status,
      code: problem?.code,
    });
  }

  const blob = await response.blob();
  const name = filenameFrom(response.headers.get('content-disposition'), fallback);

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();

  /**
   * Released on the next tick rather than immediately.
   *
   * Revoking in the same turn as the click races the browser's own handling
   * of it, and the download silently does nothing — in some browsers always,
   * in others only sometimes, which is worse.
   */
  setTimeout(() => URL.revokeObjectURL(url), 0);

  return name;
}
